import { configureStore } from '@reduxjs/toolkit';
import { Middleware } from 'redux';
import {
  createMigrate,
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import rootReducer from '../slices';
import { invoicesApi } from '../api/invoices';
import { vendorApi } from '../api/vendor';
import { paymentsApi } from '../api/payments';
import { collectionsApi } from '../api/collections';
import { externalVendorsApi } from '../api/externalVendors';
import { isRejectedAction } from '../slices/types';
import { migrations } from './migrations';

const persistConfig = {
  key: 'root',
  version: 36,
  storage,
  whitelist: [
    'advancedFilters',
    'collections',
    'adjustments',
    'demoMode',
    'invoices',
    'directDebit',
    'modals',
    'onboarding',
    'payments',
    'selectedPartner',
    'ui',
    'vendor',
  ],
  migrate: createMigrate(migrations, { debug: false }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const rtkQueryErrorHandler: Middleware = () => (next) => (action: any) => {
  if (isRejectedAction(action as any) && typeof action.error === 'object') {
    const errorDetails = action.error as Error;
    const error = new Error(errorDetails?.message);

    error.stack = errorDetails?.stack;
    error.name = errorDetails?.name;

    console.error(error);
  }

  return next(action);
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      invoicesApi.middleware,
      vendorApi.middleware,
      paymentsApi.middleware,
      collectionsApi.middleware,
      externalVendorsApi.middleware,
      rtkQueryErrorHandler,
    ),
});

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
